/**
 * Gives each contact object on a ClientConnect intake screen a ContactIdentity for the life
 * of the page load, so its per-client custom field values are read and written under a key
 * that does not move while the name is being edited.
 *
 * The identity is attached to the contact object itself (a WeakMap, so nothing is added to
 * the data that gets submitted). The intake screens mutate the same contact objects while
 * the user types, so the identity follows the contact through edits, reordering and removal.
 */

import { CONTACT_ARRAYS, contactDisplayName, newContactKey } from './perClientCustomFields';
import type { ContactArray, ContactIdentity, ContactLike, FileContacts } from './perClientCustomFields';

const identities = new WeakMap<object, ContactIdentity>();

let newContacts = 0;

export const registerContactIdentity = (contact: object, key: string, names: string[]): ContactIdentity => {
    const identity = { key, names };
    identities.set(contact, identity);
    return identity;
};

/**
 * Registers every contact of a freshly loaded file. `file` is what the screen shows (pending
 * client changes applied); `storedNames` are the display names on the file as stored, so a
 * contact with a pending rename is also known by its file name.
 */
export const registerFileContacts = (file: FileContacts, storedNames: Partial<Record<ContactArray, string[]>>): void => {
    for (const array of CONTACT_ARRAYS) {
        const contacts = file?.[array];
        if (!Array.isArray(contacts)) {
            continue;
        }
        contacts.forEach((contact: unknown, index) => {
            if (contact === null || typeof contact !== 'object') {
                return;
            }
            const shown = contactDisplayName(contact as ContactLike);
            const onFile = storedNames[array]?.[index] ?? '';
            const key = shown || onFile || newContactKey(++newContacts);
            registerContactIdentity(contact, key, onFile && onFile !== key ? [ onFile ] : []);
        });
    }
};

/** The identity a contact was registered with, or undefined for one added on this screen
 *  that has nothing written yet. */
export const contactIdentityOf = (contact: unknown): ContactIdentity | undefined =>
    contact !== null && typeof contact === 'object' ? identities.get(contact) : undefined;

/** The identity to write under; a contact added on this screen gets a placeholder key. */
export const contactIdentityFor = (contact: unknown): ContactIdentity => {
    if (contact === null || typeof contact !== 'object') {
        return { key: newContactKey(++newContacts), names: [] };
    }
    return identities.get(contact) ?? registerContactIdentity(contact, newContactKey(++newContacts), []);
};
